import "@jammable/ui-core/styles/globals.css";
import "@jammable/ui-core/styles/legacy-globals.css";

import Layout from "../components/Layout";
import { AuthProvider } from "../context/AuthProvider";
import { useEffect, useState } from "react";
import nProgress from "nprogress";
import "../styles/nprogress.css";
import { useRouter } from "next/router";
import { ModalProvider } from "../context/ModalContext";
import { QueryClient, QueryClientProvider } from "react-query";
import { DefaultSeo } from "next-seo";
import Head from "next/head";
import Script from "next/script";
import { Bricolage_Grotesque, Manrope } from "next/font/google";
import { GoogleAnalytics } from "nextjs-google-analytics";

import { AnalyticsProvider } from "../context/AnalyticsContext";
import { Toaster } from "react-hot-toast";
import GlobalAudioPlayer from "../components/GlobalAudioPlayer";

// Design language F: Manrope for everything, Bricolage Grotesque for headings
// (opt in with the `jam-display` class). Replaces Gilmer, which was forced onto
// every element with !important, so the override below stays !important too or
// old components with their own font-family would win.
const manrope = Manrope({
  subsets: ["latin"],
  weight: ["500", "600", "700", "800"],
  display: "swap",
  fallback: ["system-ui", "-apple-system", "Segoe UI", "Arial", "sans-serif"],
});
const bricolage = Bricolage_Grotesque({
  subsets: ["latin"],
  weight: ["700", "800"],
  display: "swap",
  fallback: ["system-ui", "-apple-system", "Segoe UI", "Arial", "sans-serif"],
});

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  // Create the QueryClient once for the app's lifetime. Instantiating it in the
  // render body made a fresh client (and empty cache) on every re-render,
  // forcing redundant refetches and extra main-thread work (hurts INP).
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  );

  useEffect(() => {
    nProgress.configure({ easing: "ease", speed: 500 });
    const handleStart = () => nProgress.start();

    const handleComplete = () => nProgress.done();

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  }, []);

  // Came from a Google ad: a click id on this URL, or the ad-click cookies
  // gtag/the linker wrote on an earlier landing. Decides whether the GTM
  // Conversion Linker loads (see below).
  const [adVisitor, setAdVisitor] = useState(false);
  useEffect(() => {
    setAdVisitor(
      /[?&](gclid|gbraid|wbraid|dclid|gad_source)=/.test(
        window.location.search,
      ) || /(?:^|;\s*)_gcl_(aw|gb|dc|ag)=/.test(document.cookie),
    );
  }, []);

  return (
    <div className={`${manrope.className}`}>
      <Toaster />
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0"
          />
        </Head>
        {/* GTM-5KTN4FJ7 holds one tag, a Conversion Linker, but gtm.js is
            ~148 KB and ran on every page just as people start tapping
            (INP). The GA4 gtag below already stores a gclid in _gcl_aw
            itself. What only the linker does is Google Ads' own pings
            (google.com/ccm/collect, with the click id) and the _gcl_au
            cookie, so it still loads for visitors who came from an ad:
            a click id in the URL, or an ad-click cookie from an earlier
            landing. Organic visitors (most traffic) skip it. */}
        {adVisitor && (
          <Script
            id="google-tag-manager"
            strategy="lazyOnload"
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-5KTN4FJ7');`,
            }}
          />
        )}
        <Script
          strategy="lazyOnload"
          async
          src="https://cdn.tolt.io/tolt.js"
          data-tolt="e257e435-52b6-4337-a81a-c596adaa7b3a"
        ></Script>
        <QueryClientProvider client={queryClient}>
          <GlobalAudioPlayer>
            <AuthProvider>
              <AnalyticsProvider>
                <ModalProvider>
                  {/* lazyOnload defers only gtag.js (~190 KB) until after
                      the load event. The component's inline stub defining
                      window.gtag still runs right after hydration, so event()
                      calls made before gtag.js arrives (thankyou's purchase)
                      queue in dataLayer and are sent when it does. Except
                      on /thankyou, where the purchase conversion should not
                      depend on the visitor staying until the page is idle. */}
                  <GoogleAnalytics
                    gaMeasurementId="G-TBSH1D6SKK"
                    trackPageViews
                    strategy={
                      router.pathname === "/thankyou"
                        ? "afterInteractive"
                        : "lazyOnload"
                    }
                  />
                  <DefaultSeo
                    title="Jammable | Create AI Covers With Your Favorite Voices"
                    description="Create AI covers using AI in seconds with Jammable, with thousands of community uploaded AI voice models available for creative use now!"
                    themeColor="#782DC8"
                    openGraph={{
                      title:
                        "Jammable | Create AI Covers with Your Favorite Voices | AI-Powered Music Creation",
                      description:
                        "Create AI covers using AI in seconds with Jammable, with thousands of community uploaded AI voice models available for creative use now!",
                      type: "website",
                      locale: "en_US",
                      siteName: "Jammable AI",
                    }}
                    twitter={{
                      handle: "@jammable",
                      site: "@jammable",
                      cardType: "summary_large_image",
                    }}
                  />
                  <style
                    dangerouslySetInnerHTML={{
                      __html: `
                          * {
                            font-family: ${manrope.style.fontFamily} !important;
                          }
                          .jam-display, .jam-display * {
                            font-family: ${bricolage.style.fontFamily} !important;
                            letter-spacing: -0.04em;
                          }
                          pre, pre *, code {
                            font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important;
                          }`,
                    }}
                  />
                  <Layout>
                    <Component {...pageProps} />
                  </Layout>
                </ModalProvider>
              </AnalyticsProvider>
            </AuthProvider>
          </GlobalAudioPlayer>
        </QueryClientProvider>
    </div>
  );
}

export default MyApp;
